/* eslint-disable react-refresh/only-export-components --
   This is the application bootstrap entry (calls ReactDOM.createRoot().render()
   at module load and exports nothing); react-refresh's component-export rule
   does not apply to it. `ProtectedRoute` is a local route wrapper used only here. */
import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './app.css';
import { CeDashboardPageSkeleton } from './CeDashboardPageSkeleton';
import { getCookie } from './cookieUtils';
import { installCardSelection } from './cardSelection';
import { AnalyticsConsentCard } from './components/AnalyticsConsentCard';

const CeDashboardPage = React.lazy(() => import('./CeDashboardPage'));
const CeLoginPage = React.lazy(() => import('./CeLoginPage'));
const CeWelcomePage = React.lazy(() => import('./CeWelcomePage'));

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    setIsAuthenticated(!!getCookie('complyedge-signed-in'));
  }, []);

  if (isAuthenticated === null) {
    return <CeDashboardPageSkeleton />;
  }

  if (!isAuthenticated) {
    return (
      <React.Suspense fallback={<CeDashboardPageSkeleton />}>
        <CeLoginPage onLoginSuccess={() => setIsAuthenticated(true)} />
      </React.Suspense>
    );
  }

  return <React.Suspense fallback={<CeDashboardPageSkeleton />}>{children}</React.Suspense>;
};

const rootBasename = import.meta.env.BASE_URL.replace(/\/$/, '') || '/';

const rootElement = document.getElementById('root');
if (rootElement) {
  installCardSelection();
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <BrowserRouter basename={rootBasename}>
        <Routes>
          <Route
            path="/welcome"
            element={
              <ProtectedRoute>
                <CeWelcomePage />
              </ProtectedRoute>
            }
          />
          {/* /account is a child route so the dashboard stays mounted under
              the account dialog instead of being replaced by another page. */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <CeDashboardPage />
              </ProtectedRoute>
            }
          >
            <Route path="account" element={null} />
            {/* One route per dashboard section; Account hangs under each. */}
            <Route path=":section" element={null} />
            <Route path=":section/account" element={null} />
            {/* One flag, opened by its own URL over the Audit page: the address
                an oversight record names as what the reviewer saw. */}
            <Route path=":section/flags/:flagId" element={null} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {/* Analytics choice on every screen, sign-in included: funnel events
            send nothing until it is accepted (analyticsConsent.ts). */}
        <AnalyticsConsentCard />
      </BrowserRouter>
    </React.StrictMode>
  );
} else {
  throw new Error('Root element not found');
}
